export class PublicationsList extends HTMLElement {
    private items: any[] = []
    private hasLoaded = false
    private hasMore = false
    private offset = 0
    private isLoading = false

    private readonly PAGE_SIZE = 3
    private readonly DATE_FILTER_ATTR = 'data-date-filter'
    private readonly TAG_IDS_ATTR = 'data-tag-ids'
    private readonly TODAY_ATTR = 'data-today'
    private readonly REFERENCE_LINK_TEXT_ATTR = 'data-reference-link-text'
    private readonly LOAD_MORE_TEXT_ATTR = 'data-load-more-text'
    private readonly NO_RESULTS_TEXT_ATTR = 'data-no-results-text'

    connectedCallback() {
        this.fetchEntries(false)
    }

    private fetchEntries(append: boolean) {
        if (this.isLoading) return
        this.isLoading = true

        const list = this.querySelector('.list')
        if (list) list.setAttribute('aria-busy', 'true')

        if (!append) {
            this.offset = 0
            this.items = []
        }

        const dateFilter = this.getAttribute(this.DATE_FILTER_ATTR) ?? ''
        const today = this.getAttribute(this.TODAY_ATTR) ?? ''
        const rawTagIds = this.getAttribute(this.TAG_IDS_ATTR) ?? ''

        const payload: any = {
            section: 'publications',
            limit: this.PAGE_SIZE,
            offset: this.offset,
        }

        if (dateFilter === 'past' && today) {
            payload.to = today
        } else if (dateFilter === 'next' && today) {
            payload.from = today
        }

        if (rawTagIds) {
            try {
                const tagIds: string[] = JSON.parse(rawTagIds)
                if (tagIds.length > 0) {
                    payload.categories = { tags: tagIds }
                }
            } catch {
                // malformed — ignore
            }
        }

        fetch('/api/entries', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        })
            .then(async (response) => {
                this.hasLoaded = true
                this.isLoading = false
                const text = await response.text()
                let result: any
                try {
                    result = JSON.parse(text)
                } catch {
                    this.hasMore = false
                    this.renderResult([], append)
                    return
                }
                const entries = result?.items
                if (!Array.isArray(entries)) {
                    this.hasMore = false
                    this.renderResult([], append)
                    return
                }
                const newItems = entries.map((entry: any) => ({
                    title: entry.title || '',
                    date: entry.date || '',
                    tags: Array.isArray(entry.tags) ? entry.tags : [],
                    text: entry.teaserText || entry.mehrzeiligerText || '',
                    reference: entry.reference
                        ? {
                              url: entry.reference.url || '',
                              customLabel: entry.reference.customLabel || '',
                              ariaLabel: entry.reference.ariaLabel || '',
                              type: entry.reference.type || 'url',
                          }
                        : null,
                }))
                this.items = append ? [...this.items, ...newItems] : newItems
                this.offset = this.items.length
                this.hasMore = result.hasMore === true
                this.renderResult(newItems, append)
            })
            .catch(() => {
                this.isLoading = false
                this.hasMore = false
                this.renderResult([], append)
            })
    }

    private renderItem(item: any, referenceLinkText: string): string {
        const referenceHtml = item.reference
            ? `<div class="oi-buttons mt-listMt">
                <a href="${item.reference.url}" class="button text-bodysmall"
                   ${item.reference.type === 'asset' ? 'download' : ''}
                   aria-label="${item.reference.ariaLabel || item.reference.customLabel || referenceLinkText}">
                    <b class="text-bodysmall">${item.reference.customLabel || referenceLinkText}</b>
                    <i class="small-icon download-icon"></i>
                </a>
            </div>`
            : ''

        const tagsHtml =
            item.tags.length > 0
                ? `<div class="oi-tags shrink-0 pt-1" aria-label="${item.tags.map((t: any) => t.title).join(', ')}">
                    ${item.tags.map((t: any) => `<span class="oi-tag" aria-hidden="true">${t.title}</span>`).join('')}
                </div>`
                : ''

        const year = item.date ? item.date.split('-')[0] : ''

        return `<li class="border-t border-darkgrey pt-listPt last:mb-0 mb-listMb">
            <div class="flex flex-col md:flex-row justify-between items-start gap-x-6 gap-y-3">
                <div class="flex-1 min-w-0">
                    ${year ? `<div class="text-date">${year}</div>` : ''}
                </div>
                ${tagsHtml}
            </div>
            <div class="oi-richtext mt-listMt">
                <h2 class="font-sans font-bold text-base !mb-0">${item.title}</h2>
                ${item.text ? `<p class="text-listtext whitespace-pre-line !my-0 !max-w-full pt-textGap">${item.text}</p>` : ''}
                ${referenceHtml}
            </div>
        </li>`
    }

    private renderResult(newItems: any[], append: boolean) {
        const list = this.querySelector('.list')
        const loadMoreContainer = this.querySelector('.load-more-container')
        if (!list) return

        list.setAttribute('aria-busy', 'false')

        const noResultsText = this.getAttribute(this.NO_RESULTS_TEXT_ATTR) ?? 'Keine Publikationen gefunden.'
        const referenceLinkText = this.getAttribute(this.REFERENCE_LINK_TEXT_ATTR) ?? 'Referenz herunterladen'
        const loadMoreText = this.getAttribute(this.LOAD_MORE_TEXT_ATTR) ?? 'Mehr anzeigen'

        if (!append) {
            if (this.items.length > 0) {
                list.innerHTML = this.items.map((item) => this.renderItem(item, referenceLinkText)).join('')
            } else if (this.hasLoaded) {
                list.innerHTML = `<li class="py-8 text-base">${noResultsText}</li>`
            } else {
                list.innerHTML = ''
            }
        } else if (newItems.length > 0) {
            list.insertAdjacentHTML(
                'beforeend',
                newItems.map((item) => this.renderItem(item, referenceLinkText)).join(''),
            )
        }

        if (loadMoreContainer) {
            if (this.hasMore) {
                loadMoreContainer.innerHTML = `
                    <button type="button" class="load-more-btn group mx-auto flex flex-col items-center cursor-pointer text-bodysmall text-darkgrey">
                        <span class="text-loadmore">${loadMoreText}</span>
                        <span class="flex items-center mt-listMt justify-center rounded-full border border-darkgrey py-[var(--sp-loadmore-py)] px-[var(--sp-loadmore-px)] transition-colors duration-200 group-hover:bg-green group-hover:border-green">
                            <svg width="24" height="24" viewBox="0 0 28 25" fill="none" xmlns="http://www.w3.org/2000/svg" class="rotate-90 transition-transform duration-500 group-hover:translate-y-1" aria-hidden="true">
                                <path d="M15.0252 23.6878L26.6924 12.0205L15.0252 0.353243M26.6924 12.0205L0 12.0205" stroke="currentColor"/>
                            </svg>
                        </span>
                    </button>
                `
                loadMoreContainer.querySelector('button')?.addEventListener('click', () => {
                    this.fetchEntries(true)
                })
            } else {
                loadMoreContainer.innerHTML = ''
            }
        }
    }
}
